import {
  BufferingIndicator,
  Container,
  Controls,
  createPlayer,
  Dialog,
  FullscreenButton,
  Hotkey,
  PiPButton,
  PlayButton,
  Popover,
  selectBuffer,
  selectPlayback,
  selectPlaybackRate,
  selectTime,
  selectVolume,
  Slider,
  Time,
  Tooltip,
  VolumeSlider,
} from '@videojs/react';
import { Video, videoFeatures } from '@videojs/react/video';
import {
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Copy,
  Download,
  ExternalLink,
  Fullscreen,
  Keyboard,
  ListVideo,
  LoaderCircle,
  Maximize,
  MessageSquareText,
  Minimize,
  Minus,
  MousePointer2,
  Pause,
  PictureInPicture,
  PictureInPicture2,
  Play,
  Plus,
  Settings,
  Shrink,
  Smartphone,
  Upload,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import ASS from 'assjs';
import Danmaku from 'danmaku';
import { type CSSProperties, type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import potPlayerIcon from '../assets/potplayer.webp';
import vlcIcon from '../assets/vlc.svg';
import { Alert } from './alert';
import { Button } from './button';
import { useApp } from './app-context';
import { Switch } from './switch';
import { useStore } from '../hooks/use-store';
import { t } from '../i18n';
import {
  $autoplay,
  $danmakuFontSize,
  $danmakuOutlineEnabled,
  $danmakuShadowEnabled,
  $danmakuSpeed,
  $danmakuVisible,
  $playbackRate,
  $subtitleDelay,
  $subtitleLanguage,
  $subtitlesEnabled,
  setDanmakuFontSize,
  setDanmakuOutlineEnabled,
  setDanmakuShadowEnabled,
  setDanmakuSpeed,
  setDanmakuVisible,
  setPlaybackRate,
  setSubtitleDelay,
  type PlayerSettings,
  type SubtitleLanguage,
} from '../store/app.store';
import type { PlaybackSubtitleTrack } from '../types/page';

const { Player: PlayerProvider, usePlayer } = createPlayer({
  features: videoFeatures,
  displayName: 'PlaybackVideo',
});

const matchesSubtitleLanguage = (track: PlaybackSubtitleTrack, preference: SubtitleLanguage): boolean => {
  if (preference === 'auto') {
    return track.default || track.forced;
  }
  const language = track.language?.toLowerCase().replaceAll('_', '-') ?? '';
  const label = track.label.toLowerCase();
  if (preference === 'zh-CN') {
    return (
      ['zh-cn', 'zh-sg', 'zh-hans', 'chs', 'chi', 'zho'].includes(language) ||
      label.includes('简') ||
      label.includes('簡體') ||
      label.includes('简体') ||
      label.includes('simplified')
    );
  }
  if (preference === 'zh-TW') {
    return (
      ['zh-tw', 'zh-hk', 'zh-mo', 'zh-hant', 'cht'].includes(language) ||
      label.includes('繁') ||
      label.includes('正體') ||
      label.includes('正体') ||
      label.includes('traditional')
    );
  }
  if (preference === 'ja') {
    return ['ja', 'jpn'].includes(language) || label.includes('日文') || label.includes('日本語');
  }
  return ['en', 'eng'].includes(language) || label.includes('english') || label.includes('英文');
};

const preferredSubtitle = (
  subtitles: PlaybackSubtitleTrack[],
  enabled: boolean,
  language: SubtitleLanguage,
): PlaybackSubtitleTrack | undefined => {
  if (!enabled) {
    return undefined;
  }
  const availableSubtitles = subtitles.filter((track) => track.revision > 0);
  return (
    availableSubtitles.find((track) => matchesSubtitleLanguage(track, language)) ??
    availableSubtitles.find((track) => track.default || track.forced) ??
    availableSubtitles[0]
  );
};

type DanmakuMode = 'bottom' | 'ltr' | 'rtl' | 'top';

interface DanmakuComment {
  color: string;
  mode: DanmakuMode;
  text: string;
  time: number;
}

const danmakuMode = (value: number): DanmakuMode | undefined => {
  if (value === 4) {
    return 'bottom';
  }
  if (value === 5) {
    return 'top';
  }
  if (value === 6) {
    return 'ltr';
  }
  if (value === 7 || value === 8) {
    return undefined;
  }
  return 'rtl';
};

const danmakuColor = (value: string | undefined): string => {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? `#${(Math.max(0, Math.min(parsed, 0xffffff)) >>> 0).toString(16).padStart(6, '0')}`
    : '#ffffff';
};

const parseDanmakuXml = (source: string): DanmakuComment[] => {
  const documentNode = new DOMParser().parseFromString(source, 'application/xml');
  if (documentNode.querySelector('parsererror')) {
    throw new Error(t('invalidDanmakuXml'));
  }
  const comments = [...documentNode.querySelectorAll('d[p]')].flatMap((node) => {
    const parameters = node.getAttribute('p')?.split(',') ?? [];
    const time = Number(parameters[0]);
    const mode = danmakuMode(Number(parameters[1]));
    const text = node.textContent?.trim();
    if (!Number.isFinite(time) || time < 0 || !mode || !text) {
      return [];
    }
    return [{ color: danmakuColor(parameters[3]), mode, text, time }];
  });
  if (comments.length === 0) {
    throw new Error(t('noDanmakuComments'));
  }
  return comments;
};

const DANMAKU_OUTLINE_TEXT_SHADOW = '#000 1px 0 0, #000 0 1px 0, #000 -1px 0 0, #000 0 -1px 0';
const DANMAKU_DROP_SHADOW = 'drop-shadow(0 2px 4px rgb(0 0 0 / 0.8))';

interface VideoPlayerProps {
  src: string;
  duration?: number;
  initialTime?: number;
  subtitles: PlaybackSubtitleTrack[];
  subtitlesComplete: boolean;
  onError: () => void;
  onEnded?: () => void;
  onCompleted?: () => void;
  onCopyVideoLink?: () => void;
  onDownloadM3uPlaylist?: () => void;
  onDownloadVideoFile?: () => void;
  onProgress?: (progress: number, duration: number, force: boolean) => void;
}

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '00:00';
  }
  const value = Math.floor(seconds);
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const remainder = value % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
    : `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const rangeEnd = (ranges: [number, number][]): number => ranges.reduce((maximum, [, end]) => Math.max(maximum, end), 0);

const rangeStart = (ranges: [number, number][]): number => ranges[0]?.[0] ?? 0;

const isPlayerGestureTarget = (target: EventTarget | null, container: HTMLElement | null): boolean => {
  if (!(target instanceof Element) || !container?.contains(target)) {
    return false;
  }
  return !target.closest(
    'button, a, input, select, textarea, [contenteditable="true"], [role="button"], [role="slider"], .video-time-slider, .video-volume-slider, .video-settings-popover, .video-danmaku-popover, .video-tooltip, .video-operation-guide, .video-operation-guide-backdrop, .video-external-dialog, .video-external-dialog-backdrop',
  );
};

const clampSeekTime = (
  currentTime: number,
  offset: number,
  ranges: [number, number][],
  duration?: number,
): number | undefined => {
  const firstRange = ranges[0];
  const lastRange = ranges.at(-1);
  const finiteDuration = duration && Number.isFinite(duration) ? duration : undefined;
  const minimum = finiteDuration ? 0 : firstRange?.[0];
  const maximum = finiteDuration ?? lastRange?.[1];
  if (minimum === undefined || maximum === undefined) {
    return undefined;
  }
  return Math.min(Math.max(currentTime + offset, minimum), maximum);
};

interface PlayerInteractionsProps {
  containerRef: RefObject<HTMLDivElement | null>;
  disabled: boolean;
}

const PlayerInteractions = ({ containerRef, disabled }: PlayerInteractionsProps) => {
  const store = usePlayer();
  const buffer = usePlayer(selectBuffer);
  const playback = usePlayer(selectPlayback);
  const playbackRate = usePlayer(selectPlaybackRate);
  const time = usePlayer(selectTime);
  const volume = usePlayer(selectVolume);
  const [gestureVolume, setGestureVolume] = useState<number>();
  const interactionState = useRef({ buffer, playback, playbackRate, time, volume });
  interactionState.current = { buffer, playback, playbackRate, time, volume };

  useEffect(() => {
    if (disabled) {
      return;
    }
    let longPressTimer: ReturnType<typeof setTimeout> | undefined;
    let longPressPointerId: number | undefined;
    let longPressPointerType = '';
    let longPressStart: { x: number; y: number } | undefined;
    let longPressActive = false;
    let previousPlaybackRate = 1;
    let volumeGestureActive = false;
    let volumeGestureInitialValue = 1;
    let volumeGestureUnmuted = false;
    let seekGestureActive = false;
    let seekGestureInitialTime = 0;
    let seekGestureTargetTime: number | undefined;
    let suppressClicksUntil = 0;
    let suppressDoubleClickUntil = 0;
    let singleTapTimer: ReturnType<typeof setTimeout> | undefined;
    let lastTouchTapAt = 0;
    let lastTouchTapPosition: { x: number; y: number } | undefined;
    const container = containerRef.current;
    const clearLongPressTimer = (): void => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = undefined;
      }
    };
    const clearSingleTapTimer = (): void => {
      if (singleTapTimer) {
        clearTimeout(singleTapTimer);
        singleTapTimer = undefined;
      }
    };
    const finishLongPress = (commitSeek = false): void => {
      clearLongPressTimer();
      const gestureActive = longPressActive || volumeGestureActive || seekGestureActive;
      const pointerId = longPressPointerId;
      if (longPressActive) {
        interactionState.current.playbackRate?.setPlaybackRate(previousPlaybackRate);
        container?.removeAttribute('data-temporary-rate');
      }
      if (commitSeek && seekGestureActive && seekGestureTargetTime !== undefined) {
        void store.seek(seekGestureTargetTime);
      }
      setGestureVolume(undefined);
      container?.removeAttribute('data-gesture-seek');
      if (gestureActive) {
        suppressClicksUntil = performance.now() + 400;
      }
      longPressActive = false;
      volumeGestureActive = false;
      seekGestureActive = false;
      seekGestureTargetTime = undefined;
      longPressPointerId = undefined;
      longPressPointerType = '';
      longPressStart = undefined;
      if (pointerId !== undefined && container?.hasPointerCapture(pointerId)) {
        container.releasePointerCapture(pointerId);
      }
    };
    const handleDoubleClick = (event: MouseEvent): void => {
      if (!isPlayerGestureTarget(event.target, container)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (performance.now() < suppressDoubleClickUntil) {
        return;
      }
      void interactionState.current.playback?.togglePaused();
    };
    const handlePointerDown = (event: PointerEvent): void => {
      if (
        !isPlayerGestureTarget(event.target, container) ||
        !event.isPrimary ||
        (event.pointerType === 'mouse' && event.button !== 0)
      ) {
        return;
      }
      finishLongPress();
      longPressPointerId = event.pointerId;
      longPressPointerType = event.pointerType;
      longPressStart = { x: event.clientX, y: event.clientY };
      try {
        container?.setPointerCapture(event.pointerId);
      } catch {
        // Window-level pointer listeners keep the gesture working when capture is unavailable.
      }
      if (event.pointerType === 'touch') {
        event.preventDefault();
      }
      if (event.pointerType === 'touch' && singleTapTimer) {
        clearSingleTapTimer();
      }
      volumeGestureInitialValue = interactionState.current.volume?.muted
        ? 0
        : (interactionState.current.volume?.volume ?? 1);
      seekGestureInitialTime = interactionState.current.time?.currentTime ?? 0;
      seekGestureTargetTime = undefined;
      volumeGestureUnmuted = false;
      longPressTimer = setTimeout(() => {
        if (interactionState.current.playback?.paused !== false) {
          longPressTimer = undefined;
          return;
        }
        previousPlaybackRate = interactionState.current.playbackRate?.playbackRate ?? 1;
        interactionState.current.playbackRate?.setPlaybackRate(2);
        container?.setAttribute('data-temporary-rate', 'true');
        longPressActive = true;
        longPressTimer = undefined;
      }, 500);
    };
    const handlePointerMove = (event: PointerEvent): void => {
      if (event.pointerId !== longPressPointerId || !longPressStart) {
        return;
      }
      if (longPressActive) {
        return;
      }
      const deltaX = event.clientX - longPressStart.x;
      const deltaY = event.clientY - longPressStart.y;
      const updateVolumeGesture = (): void => {
        clearLongPressTimer();
        const currentVolume = interactionState.current.volume;
        if (!currentVolume || !container) {
          return;
        }
        const nextVolume = Math.min(Math.max(volumeGestureInitialValue - deltaY / container.clientHeight, 0), 1);
        if (currentVolume.muted && nextVolume > 0 && !volumeGestureUnmuted) {
          currentVolume.toggleMuted();
          volumeGestureUnmuted = true;
        }
        currentVolume.setVolume(nextVolume);
        setGestureVolume(Math.round(nextVolume * 100));
        volumeGestureActive = true;
        event.preventDefault();
      };
      const updateSeekGesture = (): void => {
        clearLongPressTimer();
        if (!container) {
          return;
        }
        const offset = Math.min(Math.max((deltaX / container.clientWidth) * 90, -90), 90);
        const nextTime = clampSeekTime(
          seekGestureInitialTime,
          offset,
          interactionState.current.buffer?.seekable ?? [],
          interactionState.current.time?.duration,
        );
        seekGestureActive = true;
        event.preventDefault();
        if (nextTime === undefined) {
          return;
        }
        seekGestureTargetTime = nextTime;
        const roundedOffset = Math.round(nextTime - seekGestureInitialTime);
        const offsetLabel = `${roundedOffset >= 0 ? '+' : ''}${roundedOffset}s`;
        container.setAttribute('data-gesture-seek', `${offsetLabel}  ${formatTime(nextTime)}`);
      };
      if (volumeGestureActive) {
        updateVolumeGesture();
        return;
      }
      if (seekGestureActive) {
        updateSeekGesture();
        return;
      }
      if (longPressPointerType === 'touch' && Math.abs(deltaY) > 10 && Math.abs(deltaY) > Math.abs(deltaX)) {
        updateVolumeGesture();
        return;
      }
      if (longPressPointerType === 'touch' && Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
        updateSeekGesture();
        return;
      }
      if (Math.hypot(deltaX, deltaY) > 12) {
        finishLongPress();
      }
    };
    const handlePointerEndCapture = (event: PointerEvent): void => {
      if (event.pointerId !== longPressPointerId) {
        return;
      }
      const gestureActive = longPressActive || volumeGestureActive || seekGestureActive;
      const tapPosition = longPressStart;
      const isTouch = longPressPointerType === 'touch';
      if (!isTouch && !gestureActive) {
        return;
      }
      finishLongPress(true);
      event.preventDefault();
      event.stopImmediatePropagation();
      if (gestureActive || !isTouch || !tapPosition) {
        if (isTouch) {
          clearSingleTapTimer();
          lastTouchTapAt = 0;
          lastTouchTapPosition = undefined;
        }
        return;
      }
      const now = performance.now();
      const isDoubleTap =
        lastTouchTapPosition !== undefined &&
        now - lastTouchTapAt <= 300 &&
        Math.hypot(tapPosition.x - lastTouchTapPosition.x, tapPosition.y - lastTouchTapPosition.y) <= 48;
      if (!isDoubleTap) {
        lastTouchTapAt = now;
        lastTouchTapPosition = tapPosition;
        suppressClicksUntil = now + 400;
        singleTapTimer = setTimeout(() => {
          lastTouchTapAt = 0;
          lastTouchTapPosition = undefined;
          singleTapTimer = undefined;
          store.toggleControls();
        }, 300);
        return;
      }
      clearSingleTapTimer();
      lastTouchTapAt = 0;
      lastTouchTapPosition = undefined;
      suppressClicksUntil = now + 400;
      suppressDoubleClickUntil = now + 400;
      event.preventDefault();
      event.stopPropagation();
      void interactionState.current.playback?.togglePaused();
    };
    const handlePointerEnd = (event: PointerEvent): void => {
      if (event.pointerId === longPressPointerId) {
        finishLongPress(true);
      }
    };
    const handlePointerCancel = (event: PointerEvent): void => {
      if (event.pointerId !== longPressPointerId) {
        return;
      }
      finishLongPress(true);
      clearSingleTapTimer();
      lastTouchTapAt = 0;
      lastTouchTapPosition = undefined;
    };
    const handleClick = (event: MouseEvent): void => {
      if (performance.now() < suppressClicksUntil && isPlayerGestureTarget(event.target, container)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const handleContextMenu = (event: MouseEvent): void => {
      if (isPlayerGestureTarget(event.target, container)) {
        event.preventDefault();
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerCancel);
    container?.addEventListener('click', handleClick, true);
    container?.addEventListener('contextmenu', handleContextMenu);
    container?.addEventListener('dblclick', handleDoubleClick, true);
    container?.addEventListener('pointerdown', handlePointerDown);
    container?.addEventListener('pointerup', handlePointerEndCapture, true);
    return () => {
      finishLongPress();
      clearSingleTapTimer();
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerCancel);
      container?.removeEventListener('click', handleClick, true);
      container?.removeEventListener('contextmenu', handleContextMenu);
      container?.removeEventListener('dblclick', handleDoubleClick, true);
      container?.removeEventListener('pointerdown', handlePointerDown);
      container?.removeEventListener('pointerup', handlePointerEndCapture, true);
    };
  }, [containerRef, disabled, store]);

  return (
    <>
      <Hotkey keys="Space" action="togglePaused" disabled={disabled} />
      <Hotkey keys="k" action="togglePaused" disabled={disabled} />
      <Hotkey keys="ArrowLeft" action="seekStep" value={-5} disabled={disabled} />
      <Hotkey keys="ArrowRight" action="seekStep" value={5} disabled={disabled} />
      <Hotkey keys="ArrowUp" action="volumeStep" value={0.1} disabled={disabled} />
      <Hotkey keys="ArrowDown" action="volumeStep" value={-0.1} disabled={disabled} />
      <Hotkey keys="m" action="toggleMuted" disabled={disabled} />
      <Hotkey keys="f" action="toggleFullscreen" disabled={disabled} />
      {gestureVolume !== undefined ? (
        <div className="video-gesture-volume-indicator" aria-hidden="true">
          {gestureVolume === 0 ? <VolumeX /> : <Volume2 />}
          <span>{gestureVolume}%</span>
        </div>
      ) : null}
    </>
  );
};

const PlaybackTimeline = ({ expectedDuration }: { expectedDuration?: number }) => {
  const store = usePlayer();
  const buffer = usePlayer(selectBuffer);
  const time = usePlayer(selectTime);
  const seekable = buffer?.seekable ?? [];
  const seekableStart = rangeStart(seekable);
  const seekableEnd = rangeEnd(seekable);
  const availableDuration = Math.max(seekableEnd - seekableStart, 0);
  const currentTime = Math.max((time?.currentTime ?? seekableStart) - seekableStart, 0);
  const mediaDuration = Math.max((time?.duration ?? 0) - seekableStart, 0);
  const knownDuration = Math.max(expectedDuration ?? 0, mediaDuration, availableDuration);
  const totalDuration = Math.max(knownDuration, 1);
  const availablePercent = totalDuration > 0 ? Math.min((availableDuration / totalDuration) * 100, 100) : 0;
  const seekToValue = (value: number): void => {
    if (knownDuration <= 0) {
      return;
    }
    void store.seek(seekableStart + Math.min(Math.max(value, 0), knownDuration));
  };

  return (
    <div className="video-timeline-shell">
      <Slider.Root
        className="video-time-slider"
        label={t('playerProgress')}
        min={0}
        max={totalDuration}
        step={1}
        largeStep={10}
        value={Math.min(currentTime, totalDuration)}
        disabled={knownDuration <= 0}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          if (touch) {
            const bounds = event.currentTarget.getBoundingClientRect();
            const percent = Math.min(Math.max((touch.clientX - bounds.left) / bounds.width, 0), 1);
            seekToValue(percent * knownDuration);
          }
        }}
        onValueChange={seekToValue}
      >
        <Slider.Track className="video-slider-track">
          <span className="video-slider-available" style={{ width: `${availablePercent}%` }} />
          <Slider.Fill className="video-slider-fill" />
          <span
            className="video-timeline-unavailable"
            title={t('playerTimelineUnavailable')}
            style={{ left: `${availablePercent}%`, width: `${100 - availablePercent}%` }}
          />
        </Slider.Track>
        <Slider.Thumb className="video-slider-thumb" />
        <Slider.Preview className="video-slider-preview">
          <Slider.Value type="pointer" format={formatTime} className="video-slider-value" />
        </Slider.Preview>
      </Slider.Root>
    </div>
  );
};

const PlaybackTime = ({ expectedDuration }: { expectedDuration?: number }) => {
  const buffer = usePlayer(selectBuffer);
  const time = usePlayer(selectTime);
  const seekableStart = rangeStart(buffer?.seekable ?? []);
  const currentTime = Math.max((time?.currentTime ?? seekableStart) - seekableStart, 0);

  return (
    <Time.Group className="video-time">
      <time>{formatTime(currentTime)}</time>
      <Time.Separator className="px-1" />
      {expectedDuration ? <time>{formatTime(expectedDuration)}</time> : <Time.Value type="duration" />}
    </Time.Group>
  );
};

const VolumeControl = () => {
  const volume = usePlayer(selectVolume);
  const value = volume?.muted ? 0 : (volume?.volume ?? 1);
  const label = value === 0 ? t('playerUnmute') : t('playerMute');
  const toggleVolume = (): void => {
    if (!volume) {
      return;
    }
    if (volume.muted || volume.volume === 0) {
      volume.toggleMuted();
      volume.setVolume(1);
      return;
    }
    volume.toggleMuted();
  };

  return (
    <div className="video-volume-control">
      <Tooltip.Root side="top">
        <Tooltip.Trigger
          render={
            <button type="button" className="video-control-button" aria-label={label} onClick={toggleVolume}>
              {value === 0 ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
            </button>
          }
        />
        <Tooltip.Popup className="video-tooltip">
          <Tooltip.Arrow className="video-tooltip-arrow" />
          <Tooltip.Label>{label}</Tooltip.Label>
        </Tooltip.Popup>
      </Tooltip.Root>
      <VolumeSlider.Root className="video-volume-slider" label={t('playerVolume')}>
        <VolumeSlider.Track className="video-volume-track">
          <VolumeSlider.Fill className="video-volume-fill" />
        </VolumeSlider.Track>
        <VolumeSlider.Thumb className="video-volume-thumb" />
      </VolumeSlider.Root>
    </div>
  );
};

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;
const CONTROLS_KEEP_ALIVE_INTERVAL = 1000;

interface SettingsControlProps {
  open: boolean;
  subtitles: PlaybackSubtitleTrack[];
  subtitlesComplete: boolean;
  selectedSubtitleId: number | null;
  subtitleDelay: number;
  onCopyVideoLink?: () => void;
  onDownloadVideoFile?: () => void;
  onMenuOpenChange: (open: boolean) => void;
  onShowExternalPlayer: () => void;
  onShowOperationGuide: () => void;
  onSelectSubtitle: (id: number | null) => void;
  onSubtitleDelayChange: (delay: number) => void;
}

const SettingsControl = ({
  open,
  subtitles,
  subtitlesComplete,
  selectedSubtitleId,
  subtitleDelay,
  onCopyVideoLink,
  onDownloadVideoFile,
  onMenuOpenChange,
  onShowExternalPlayer,
  onShowOperationGuide,
  onSelectSubtitle,
  onSubtitleDelayChange,
}: SettingsControlProps) => {
  const playbackRate = usePlayer(selectPlaybackRate);
  const savedPlaybackRate = useStore($playbackRate);
  const [view, setView] = useState<'main' | 'playbackRate' | 'subtitles'>('main');
  const currentRate = playbackRate?.playbackRate ?? savedPlaybackRate;
  const availableSubtitles = subtitles.filter((track) => track.revision > 0);
  const subtitlesUnavailable = availableSubtitles.length === 0;

  return (
    <Popover.Root
      open={open}
      side="top"
      align="end"
      onOpenChange={(nextOpen) => {
        onMenuOpenChange(nextOpen);
        if (!nextOpen) {
          setView('main');
        }
      }}
    >
      <Tooltip.Root side="top">
        <Tooltip.Trigger
          render={
            <Popover.Trigger className="video-control-button" aria-label={t('settings')}>
              <Settings className="size-4" />
            </Popover.Trigger>
          }
        />
        <Tooltip.Popup className="video-tooltip">
          <Tooltip.Arrow className="video-tooltip-arrow" />
          <Tooltip.Label>{t('settings')}</Tooltip.Label>
        </Tooltip.Popup>
      </Tooltip.Root>
      <Popover.Popup className="video-settings-popover">
        <Popover.Arrow className="video-settings-arrow" />
        {view === 'main' ? (
          <>
            <Button variant="plain" className="video-settings-menu-item" onClick={() => setView('playbackRate')}>
              <span>{t('playbackSpeed')}</span>
              <span className="video-settings-menu-value">
                <span className="video-settings-menu-label">{currentRate}×</span>
                <ChevronRight />
              </span>
            </Button>
            {(subtitles.length > 0 || !subtitlesComplete) && (
              <Button
                variant="plain"
                className="video-settings-menu-item"
                disabled={subtitlesUnavailable}
                onClick={() => setView('subtitles')}
              >
                <span>{t('subtitles')}</span>
                <span className="video-settings-menu-value">
                  <span className="video-settings-menu-label">
                    {subtitlesUnavailable
                      ? t('subtitleUnavailable')
                      : selectedSubtitleId === null
                        ? t('subtitleOff')
                        : subtitles.find((track) => track.id === selectedSubtitleId)?.label}
                  </span>
                  <ChevronRight />
                </span>
              </Button>
            )}
            {onCopyVideoLink ? (
              <Button
                variant="plain"
                className="video-settings-menu-item"
                onClick={() => {
                  onMenuOpenChange(false);
                  onCopyVideoLink();
                }}
              >
                <span>{t('copyVideoLink')}</span>
                <Copy />
              </Button>
            ) : null}
            <Button
              variant="plain"
              className="video-settings-menu-item"
              onClick={() => {
                onMenuOpenChange(false);
                onShowExternalPlayer();
              }}
            >
              <span>{t('openExternalPlayer')}</span>
              <ExternalLink />
            </Button>
            {onDownloadVideoFile ? (
              <Button
                variant="plain"
                className="video-settings-menu-item"
                onClick={() => {
                  onMenuOpenChange(false);
                  onDownloadVideoFile();
                }}
              >
                <span>{t('downloadVideoFile')}</span>
                <Download />
              </Button>
            ) : null}
            <Button
              variant="plain"
              className="video-settings-menu-item"
              onClick={() => {
                onMenuOpenChange(false);
                onShowOperationGuide();
              }}
            >
              <span>{t('playerOperationGuide')}</span>
              <CircleHelp />
            </Button>
          </>
        ) : view === 'playbackRate' ? (
          <>
            <Button variant="plain" className="video-settings-back" onClick={() => setView('main')}>
              <ChevronLeft />
              {t('playbackSpeed')}
            </Button>
            <div className="video-playback-rate-options">
              {PLAYBACK_RATES.map((rate) => (
                <Button
                  key={rate}
                  variant="plain"
                  className={`video-playback-rate-option ${currentRate === rate ? 'active' : ''}`.trim()}
                  aria-pressed={currentRate === rate}
                  onClick={() => {
                    playbackRate?.setPlaybackRate(rate);
                    setPlaybackRate(rate);
                    onMenuOpenChange(false);
                  }}
                >
                  {rate}×
                </Button>
              ))}
            </div>
          </>
        ) : (
          <>
            <Button variant="plain" className="video-settings-back" onClick={() => setView('main')}>
              <ChevronLeft />
              {t('subtitles')}
            </Button>
            <div className="video-subtitle-offset">
              <span>
                {t('subtitleTimeOffset')}
                <strong>{subtitleDelay > 0 ? `+${subtitleDelay.toFixed(1)}` : subtitleDelay.toFixed(1)}s</strong>
              </span>
              <div>
                <Button
                  compact
                  className="video-subtitle-offset-button"
                  aria-label={t('subtitleTimeOffsetAhead')}
                  disabled={selectedSubtitleId === null}
                  onClick={() => onSubtitleDelayChange(Math.max(-60, subtitleDelay - 0.5))}
                >
                  <Minus className="size-3.5!" />
                </Button>
                <Button
                  compact
                  className="video-subtitle-offset-button"
                  aria-label={t('resetSubtitleTimeOffset')}
                  disabled={selectedSubtitleId === null || subtitleDelay === 0}
                  onClick={() => onSubtitleDelayChange(0)}
                >
                  0
                </Button>
                <Button
                  compact
                  className="video-subtitle-offset-button"
                  aria-label={t('subtitleTimeOffsetBehind')}
                  disabled={selectedSubtitleId === null}
                  onClick={() => onSubtitleDelayChange(Math.min(60, subtitleDelay + 0.5))}
                >
                  <Plus className="size-3.5!" />
                </Button>
              </div>
            </div>
            <div className="video-playback-rate-options">
              <Button
                variant="plain"
                className={`video-playback-rate-option ${selectedSubtitleId === null ? 'active' : ''}`.trim()}
                aria-pressed={selectedSubtitleId === null}
                onClick={() => {
                  onSelectSubtitle(null);
                  onMenuOpenChange(false);
                }}
              >
                <span className="video-settings-option-label">{t('subtitleOff')}</span>
              </Button>
              {subtitles.map((track) => (
                <Button
                  key={track.id}
                  variant="plain"
                  className={`video-playback-rate-option ${selectedSubtitleId === track.id ? 'active' : ''}`.trim()}
                  disabled={track.revision === 0}
                  aria-pressed={selectedSubtitleId === track.id}
                  onClick={() => {
                    onSelectSubtitle(track.id);
                    onMenuOpenChange(false);
                  }}
                >
                  <span className="video-settings-option-label" title={track.label}>
                    {track.label}
                    {track.revision === 0 && ` (${t('subtitleUnavailable')})`}
                  </span>
                </Button>
              ))}
            </div>
          </>
        )}
      </Popover.Popup>
    </Popover.Root>
  );
};

interface DanmakuControlProps {
  commentCount: number;
  fontSize: PlayerSettings['danmakuFontSize'];
  outlineEnabled: boolean;
  shadowEnabled: boolean;
  sourceName?: string;
  speed: PlayerSettings['danmakuSpeed'];
  visible: boolean;
  menuOpen?: boolean;
  onFontSizeChange: (value: PlayerSettings['danmakuFontSize']) => void;
  onLoadXml: (source: string, sourceName: string) => void;
  onMenuOpenChange?: (open: boolean) => void;
  onOutlineChange: (value: boolean) => void;
  onShadowChange: (value: boolean) => void;
  onSpeedChange: (value: PlayerSettings['danmakuSpeed']) => void;
  onVisibleChange: (value: boolean) => void;
}

const DanmakuControl = ({
  commentCount,
  fontSize,
  outlineEnabled,
  shadowEnabled,
  sourceName,
  speed,
  visible,
  menuOpen = false,
  onFontSizeChange,
  onLoadXml,
  onMenuOpenChange,
  onOutlineChange,
  onShadowChange,
  onSpeedChange,
  onVisibleChange,
}: DanmakuControlProps) => {
  const { showToast } = useApp();
  const [view, setView] = useState<'fontSize' | 'main' | 'speed'>('main');
  const [remoteUrl, setRemoteUrl] = useState('');
  const [loadingRemote, setLoadingRemote] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fontSizeOptions: { label: string; value: PlayerSettings['danmakuFontSize'] }[] = [
    { label: t('danmakuScaleSmall'), value: 18 },
    { label: t('danmakuScaleMedium'), value: 24 },
    { label: t('danmakuScaleLarge'), value: 32 },
    { label: t('danmakuScaleExtraLarge'), value: 42 },
  ];
  const speedOptions: { label: string; value: PlayerSettings['danmakuSpeed'] }[] = [
    { label: t('danmakuScaleSlow'), value: 84 },
    { label: t('danmakuScaleMedium'), value: 144 },
    { label: t('danmakuScaleFast'), value: 192 },
    { label: t('danmakuScaleExtraFast'), value: 240 },
  ];
  const fontSizeScaleIndex = fontSize < 22 ? 0 : fontSize < 30 ? 1 : fontSize < 40 ? 2 : 3;
  const speedScaleIndex = speed < 100 ? 0 : speed < 160 ? 1 : speed < 215 ? 2 : 3;

  const reportLoadError = (reason: unknown): void => {
    showToast(reason instanceof Error ? reason.message : String(reason), 'error');
  };
  const loadRemoteXml = async (): Promise<void> => {
    setLoadingRemote(true);
    try {
      const url = new URL(remoteUrl.trim());
      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error(t('invalidDanmakuUrl'));
      }
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`${t('danmakuLoadFailed')} (${response.status})`);
      }
      onLoadXml(await response.text(), url.href);
    } catch (reason) {
      reportLoadError(reason);
    } finally {
      setLoadingRemote(false);
    }
  };
  const loadLocalXml = async (file: File): Promise<void> => {
    try {
      onLoadXml(await file.text(), file.name);
    } catch (reason) {
      reportLoadError(reason);
    }
  };

  return (
    <Popover.Root
      open={menuOpen}
      side="top"
      align="end"
      onOpenChange={(nextOpen) => {
        onMenuOpenChange?.(nextOpen);
        if (!nextOpen) {
          setView('main');
        }
      }}
    >
      <Tooltip.Root side="top">
        <Tooltip.Trigger
          render={
            <Popover.Trigger className="video-control-button" aria-label={t('danmaku')}>
              <MessageSquareText className="size-4" />
            </Popover.Trigger>
          }
        />
        <Tooltip.Popup className="video-tooltip">
          <Tooltip.Arrow className="video-tooltip-arrow" />
          <Tooltip.Label>{t('danmaku')}</Tooltip.Label>
        </Tooltip.Popup>
      </Tooltip.Root>
      <Popover.Popup className="video-settings-popover video-danmaku-popover">
        <Popover.Arrow className="video-settings-arrow" />
        {view === 'main' ? (
          <>
            <div className="flex items-center justify-between gap-3 border-b border-white/10 px-1 pb-2">
              <span className="text-xs font-bold">{t('danmakuSettings')}</span>
              <small className="max-w-36 truncate text-[10px] text-white/50" title={sourceName}>
                {commentCount > 0
                  ? t('danmakuLoadedCount').replace('{count}', String(commentCount))
                  : t('danmakuNotLoaded')}
              </small>
            </div>
            <div className="flex min-h-10 items-center justify-between gap-3 px-2 text-xs text-white">
              <span>{t('showDanmaku')}</span>
              <Switch
                className="video-danmaku-switch"
                label={t('showDanmaku')}
                checked={visible}
                disabled={commentCount === 0}
                onChange={onVisibleChange}
              />
            </div>
            <Button variant="plain" className="video-settings-menu-item" onClick={() => setView('fontSize')}>
              <span>{t('danmakuFontSize')}</span>
              <span className="video-settings-menu-value">
                <span className="video-settings-menu-label">{fontSizeOptions[fontSizeScaleIndex]?.label}</span>
                <ChevronRight />
              </span>
            </Button>
            <Button variant="plain" className="video-settings-menu-item" onClick={() => setView('speed')}>
              <span>{t('danmakuSpeed')}</span>
              <span className="video-settings-menu-value">
                <span className="video-settings-menu-label">{speedOptions[speedScaleIndex]?.label}</span>
                <ChevronRight />
              </span>
            </Button>
            <div className="flex min-h-10 items-center justify-between gap-3 px-2 text-xs text-white">
              <span>{t('danmakuOutline')}</span>
              <Switch
                className="video-danmaku-switch"
                label={t('danmakuOutline')}
                checked={outlineEnabled}
                onChange={onOutlineChange}
              />
            </div>
            <div className="flex min-h-10 items-center justify-between gap-3 px-2 text-xs text-white">
              <span>{t('danmakuShadow')}</span>
              <Switch
                className="video-danmaku-switch"
                label={t('danmakuShadow')}
                checked={shadowEnabled}
                onChange={onShadowChange}
              />
            </div>
            <form
              className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-1.5 border-t border-white/10 pt-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (remoteUrl.trim()) {
                  void loadRemoteXml();
                }
              }}
            >
              <input
                className="min-w-0 rounded-lg border border-white/10 bg-white/8 px-2.5 text-[11px] text-white outline-none placeholder:text-white/35 focus:border-white/25"
                type="url"
                value={remoteUrl}
                placeholder={t('danmakuXmlUrl')}
                aria-label={t('danmakuXmlUrl')}
                onChange={(event) => setRemoteUrl(event.currentTarget.value)}
              />
              <Button
                compact
                loading={loadingRemote}
                disabled={!remoteUrl.trim()}
                className="border-white/10! bg-white/8! text-white!"
                type="submit"
              >
                {t('loadDanmakuXml')}
              </Button>
            </form>
            <input
              ref={fileInputRef}
              className="hidden"
              type="file"
              accept=".xml,application/xml,text/xml"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                if (file) {
                  void loadLocalXml(file);
                }
              }}
            />
            <Button
              variant="plain"
              className="video-settings-menu-item mt-1"
              onClick={() => fileInputRef.current?.click()}
            >
              <span>{t('loadLocalDanmakuXml')}</span>
              <Upload />
            </Button>
            <small className="mt-1 block px-2 text-[9px] leading-4 text-white/40">{t('danmakuCorsHint')}</small>
          </>
        ) : (
          <>
            <Button variant="plain" className="video-settings-back" onClick={() => setView('main')}>
              <ChevronLeft />
              {t(view === 'fontSize' ? 'danmakuFontSize' : 'danmakuSpeed')}
            </Button>
            <div className="video-playback-rate-options">
              {view === 'fontSize'
                ? fontSizeOptions.map((option) => (
                    <Button
                      variant="plain"
                      className={`video-playback-rate-option ${fontSize === option.value ? 'active' : ''}`.trim()}
                      aria-pressed={fontSize === option.value}
                      key={option.value}
                      onClick={() => {
                        onFontSizeChange(option.value);
                        setView('main');
                      }}
                    >
                      {option.label}
                    </Button>
                  ))
                : speedOptions.map((option) => (
                    <Button
                      variant="plain"
                      className={`video-playback-rate-option ${speed === option.value ? 'active' : ''}`.trim()}
                      aria-pressed={speed === option.value}
                      key={option.value}
                      onClick={() => {
                        onSpeedChange(option.value);
                        setView('main');
                      }}
                    >
                      {option.label}
                    </Button>
                  ))}
            </div>
          </>
        )}
      </Popover.Popup>
    </Popover.Root>
  );
};

interface PlayerOperationGuideProps {
  open: boolean;
  onClose: () => void;
}

const PlayerOperationGuide = ({ open, onClose }: PlayerOperationGuideProps) => {
  const gestureOperations = [
    [t('playerOperationTap'), t('playerOperationTapDescription')],
    [t('playerOperationDoubleTap'), t('playerOperationDoubleTapDescription')],
    [t('playerOperationLongPress'), t('playerOperationLongPressDescription')],
    [t('playerOperationVerticalSwipe'), t('playerOperationVerticalSwipeDescription')],
    [t('playerOperationHorizontalSwipe'), t('playerOperationHorizontalSwipeDescription')],
  ];
  const keyboardOperations = [
    ['Space / K', t('playerOperationPlayPause')],
    ['← / →', t('playerOperationSeek')],
    ['↑ / ↓', t('playerOperationVolume')],
    ['M', t('playerOperationMute')],
    ['F', t('playerOperationFullscreen')],
  ];

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Backdrop
        className="video-operation-guide-backdrop absolute inset-0 z-30 bg-black/70 backdrop-blur-sm transition-opacity duration-200"
        onPointerDown={onClose}
      />
      <Dialog.Popup className="video-operation-guide absolute inset-0 z-31 grid place-items-center p-4 text-white transition-opacity duration-200">
        <section className="video-operation-guide-panel flex max-h-full w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-white/15 bg-neutral-950/95 shadow-2xl">
          <header className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-5 py-4">
            <Dialog.Title className="m-0 text-base font-bold text-white">{t('playerOperationGuide')}</Dialog.Title>
            <Dialog.Close
              className="grid size-7! min-h-7! min-w-7! max-h-7! max-w-7! place-items-center rounded-full text-white! hover:bg-white/10"
              aria-label={t('closePlayerOperationGuide')}
              autoFocus
              render={<Button variant="plain" iconOnly />}
            >
              <X className="size-4!" />
            </Dialog.Close>
          </header>
          <div className="grid min-h-0 gap-5 overflow-y-auto p-5">
            <section>
              <h3 className="mb-2.5 flex items-center gap-2 text-xs font-bold text-white/75">
                <MousePointer2 className="size-4" />
                <Smartphone className="size-4" />
                <span>{t('playerGestureOperations')}</span>
              </h3>
              <dl className="grid gap-1.5">
                {gestureOperations.map(([operation, description]) => (
                  <div
                    className="grid grid-cols-[minmax(80px,0.35fr)_minmax(0,1fr)] gap-4 rounded-lg bg-white/5 px-3 py-2.5 text-xs"
                    key={operation}
                  >
                    <dt className="font-bold text-white">{operation}</dt>
                    <dd className="m-0 text-white/60">{description}</dd>
                  </div>
                ))}
              </dl>
            </section>
            <section>
              <h3 className="mb-2.5 flex items-center gap-2 text-xs font-bold text-white/75">
                <Keyboard className="size-4" />
                <span>{t('playerKeyboardOperations')}</span>
              </h3>
              <dl className="grid grid-cols-2 gap-1.5 max-[700px]:grid-cols-1">
                {keyboardOperations.map(([shortcut, description]) => (
                  <div
                    className="flex items-center justify-between gap-4 rounded-lg bg-white/5 px-3 py-2.5 text-xs"
                    key={shortcut}
                  >
                    <dt>
                      <kbd className="rounded border border-white/15 bg-white/8 px-1.5 py-0.5 font-mono font-bold text-white">
                        {shortcut}
                      </kbd>
                    </dt>
                    <dd className="m-0 text-right text-white/60">{description}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>
        </section>
      </Dialog.Popup>
    </Dialog.Root>
  );
};

interface ExternalPlayerDialogProps {
  src: string;
  open: boolean;
  onClose: () => void;
  onCopyVideoLink?: () => void;
  onDownloadM3uPlaylist?: () => void;
}

const ExternalPlayerDialog = ({
  src,
  open,
  onClose,
  onCopyVideoLink,
  onDownloadM3uPlaylist,
}: ExternalPlayerDialogProps) => {
  const openInExternalPlayer = (protocol: 'potplayer' | 'vlc'): void => {
    const videoLink = new URL(src, location.origin).href;
    location.href = `${protocol}://${videoLink}`;
  };
  const actionButtonClass = 'w-full border border-white/25!';

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Backdrop
        className="video-external-dialog-backdrop absolute inset-0 z-30 bg-black/70 backdrop-blur-sm transition-opacity duration-200"
        onPointerDown={onClose}
      />
      <Dialog.Popup className="video-external-dialog absolute inset-0 z-31 grid place-items-center p-4 text-white transition-opacity duration-200">
        <section className="flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-2xl border border-white/15 bg-neutral-950/95 shadow-2xl">
          <header className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-5 py-4">
            <Dialog.Title className="m-0 text-base font-bold text-white">{t('openExternalPlayer')}</Dialog.Title>
            <Dialog.Close
              className="grid size-7! min-h-7! min-w-7! max-h-7! max-w-7! place-items-center rounded-full text-white! hover:bg-white/10"
              aria-label={t('closeExternalPlayerDialog')}
              autoFocus
              render={<Button variant="plain" iconOnly />}
            >
              <X className="size-4!" />
            </Dialog.Close>
          </header>
          <Dialog.Description render={<div />} className="grid min-h-0 gap-4 overflow-y-auto p-5">
            <Alert variant="info">{t('externalPlaybackNotice')}</Alert>
            <div className="grid grid-cols-2 gap-2 max-[700px]:grid-cols-1">
              <Button className={actionButtonClass} onClick={() => openInExternalPlayer('potplayer')}>
                <img className="size-4 object-contain" src={potPlayerIcon} alt="" />
                <span>{t('openWithPotPlayer')}</span>
              </Button>
              <Button className={actionButtonClass} onClick={() => openInExternalPlayer('vlc')}>
                <img className="size-4 object-contain" src={vlcIcon} alt="" />
                <span>{t('openWithVlc')}</span>
              </Button>
              {onDownloadM3uPlaylist ? (
                <Button className={actionButtonClass} onClick={onDownloadM3uPlaylist}>
                  <ListVideo className="size-4!" />
                  <span>{t('downloadM3uPlaylist')}</span>
                </Button>
              ) : null}
              {onCopyVideoLink ? (
                <Button className={actionButtonClass} onClick={onCopyVideoLink}>
                  <Copy className="size-4!" />
                  <span>{t('copyVideoLink')}</span>
                </Button>
              ) : null}
            </div>
          </Dialog.Description>
        </section>
      </Dialog.Popup>
    </Dialog.Root>
  );
};

interface PlayerControlsProps {
  containerRef: RefObject<HTMLDivElement | null>;
  danmaku: DanmakuControlProps;
  expectedDuration?: number;
  webFullscreen: boolean;
  onToggleWebFullscreen: () => void;
  subtitles: PlaybackSubtitleTrack[];
  subtitlesComplete: boolean;
  selectedSubtitleId: number | null;
  subtitleDelay: number;
  onCopyVideoLink?: () => void;
  onDownloadVideoFile?: () => void;
  onShowExternalPlayer: () => void;
  onShowOperationGuide: () => void;
  onSelectSubtitle: (id: number | null) => void;
  onSubtitleDelayChange: (delay: number) => void;
}

const PlayerControls = ({
  containerRef,
  danmaku,
  expectedDuration,
  webFullscreen,
  onToggleWebFullscreen,
  subtitles,
  subtitlesComplete,
  selectedSubtitleId,
  subtitleDelay,
  onCopyVideoLink,
  onDownloadVideoFile,
  onShowExternalPlayer,
  onShowOperationGuide,
  onSelectSubtitle,
  onSubtitleDelayChange,
}: PlayerControlsProps) => {
  const [openMenu, setOpenMenu] = useState<'danmaku' | 'settings'>();
  const updateOpenMenu = (menu: 'danmaku' | 'settings', open: boolean): void => {
    setOpenMenu((current) => (open ? menu : current === menu ? undefined : current));
  };

  useEffect(() => {
    if (!openMenu) {
      return;
    }
    const keepControlsActive = (): void => {
      containerRef.current?.dispatchEvent(new PointerEvent('pointermove', { pointerType: 'mouse' }));
    };
    keepControlsActive();
    const interval = setInterval(keepControlsActive, CONTROLS_KEEP_ALIVE_INTERVAL);
    return () => clearInterval(interval);
  }, [containerRef, openMenu]);

  return (
    <Tooltip.Provider delay={400}>
      <BufferingIndicator
        className="video-buffering-indicator"
        render={(props, state) => <div {...props}>{state.visible && <LoaderCircle className="size-10 spin" />}</div>}
      />
      <Controls.Root>
        <Controls.Content className={`video-controls${openMenu ? ' menu-open' : ''}`}>
          <Controls.Group className="video-controls-content" aria-label={t('playerControls')}>
            <PlaybackTimeline expectedDuration={expectedDuration} />
            <div className="video-control-row">
              <Controls.Group className="video-control-group" aria-label={t('playerPlaybackControls')}>
                <Tooltip.Root side="top">
                  <Tooltip.Trigger
                    render={
                      <PlayButton
                        className="video-control-button"
                        label={(state) => (state.paused ? t('playerPlay') : t('playerPause'))}
                        render={(props, state) => (
                          <button {...props}>
                            {state.paused ? <Play className="size-4" /> : <Pause className="size-4" />}
                          </button>
                        )}
                      />
                    }
                  />
                  <Tooltip.Popup className="video-tooltip">
                    <Tooltip.Arrow className="video-tooltip-arrow" />
                    <Tooltip.Label />
                  </Tooltip.Popup>
                </Tooltip.Root>
                <VolumeControl />
                <PlaybackTime expectedDuration={expectedDuration} />
              </Controls.Group>
              <Controls.Group className="video-control-group" aria-label={t('playerDisplayControls')}>
                <SettingsControl
                  open={openMenu === 'settings'}
                  subtitles={subtitles}
                  subtitlesComplete={subtitlesComplete}
                  selectedSubtitleId={selectedSubtitleId}
                  subtitleDelay={subtitleDelay}
                  onCopyVideoLink={onCopyVideoLink}
                  onDownloadVideoFile={onDownloadVideoFile}
                  onMenuOpenChange={(open) => updateOpenMenu('settings', open)}
                  onShowExternalPlayer={onShowExternalPlayer}
                  onShowOperationGuide={onShowOperationGuide}
                  onSelectSubtitle={onSelectSubtitle}
                  onSubtitleDelayChange={onSubtitleDelayChange}
                />
                <DanmakuControl
                  {...danmaku}
                  menuOpen={openMenu === 'danmaku'}
                  onMenuOpenChange={(open) => updateOpenMenu('danmaku', open)}
                />
                <Tooltip.Root side="top">
                  <Tooltip.Trigger
                    render={
                      <PiPButton
                        className="video-control-button video-pip-button"
                        label={t('playerPictureInPicture')}
                        render={(props, state) => (
                          <button {...props}>
                            {state.pip ? (
                              <PictureInPicture className="size-4" />
                            ) : (
                              <PictureInPicture2 className="size-4" />
                            )}
                          </button>
                        )}
                      />
                    }
                  />
                  <Tooltip.Popup className="video-tooltip">
                    <Tooltip.Arrow className="video-tooltip-arrow" />
                    <Tooltip.Label />
                  </Tooltip.Popup>
                </Tooltip.Root>
                <Tooltip.Root side="top">
                  <Tooltip.Trigger
                    render={
                      <button
                        type="button"
                        className="video-control-button"
                        aria-label={webFullscreen ? t('playerExitWebFullscreen') : t('playerWebFullscreen')}
                        onClick={onToggleWebFullscreen}
                      >
                        {webFullscreen ? <Shrink className="size-4" /> : <Fullscreen className="size-4" />}
                      </button>
                    }
                  />
                  <Tooltip.Popup className="video-tooltip">
                    <Tooltip.Arrow className="video-tooltip-arrow" />
                    <Tooltip.Label>
                      {webFullscreen ? t('playerExitWebFullscreen') : t('playerWebFullscreen')}
                    </Tooltip.Label>
                  </Tooltip.Popup>
                </Tooltip.Root>
                <Tooltip.Root side="top">
                  <Tooltip.Trigger
                    render={
                      <FullscreenButton
                        className="video-control-button video-fullscreen-button"
                        label={(state) => (state.fullscreen ? t('playerExitFullscreen') : t('playerFullscreen'))}
                        render={(props, state) => (
                          <button {...props}>
                            {state.fullscreen ? <Minimize className="size-4" /> : <Maximize className="size-4" />}
                          </button>
                        )}
                      />
                    }
                  />
                  <Tooltip.Popup className="video-tooltip">
                    <Tooltip.Arrow className="video-tooltip-arrow" />
                    <Tooltip.Label />
                  </Tooltip.Popup>
                </Tooltip.Root>
              </Controls.Group>
            </div>
          </Controls.Group>
        </Controls.Content>
      </Controls.Root>
    </Tooltip.Provider>
  );
};

export const VideoPlayer = ({
  src,
  duration,
  initialTime,
  subtitles,
  subtitlesComplete,
  onError,
  onEnded,
  onCompleted,
  onCopyVideoLink,
  onDownloadM3uPlaylist,
  onDownloadVideoFile,
  onProgress,
}: VideoPlayerProps) => {
  const { showToast } = useApp();
  const autoplay = useStore($autoplay);
  const danmakuFontSize = useStore($danmakuFontSize);
  const danmakuOutlineEnabled = useStore($danmakuOutlineEnabled);
  const danmakuShadowEnabled = useStore($danmakuShadowEnabled);
  const danmakuSpeed = useStore($danmakuSpeed);
  const danmakuVisible = useStore($danmakuVisible);
  const savedPlaybackRate = useStore($playbackRate);
  const subtitleDelay = useStore($subtitleDelay);
  const subtitlesEnabled = useStore($subtitlesEnabled);
  const subtitleLanguage = useStore($subtitleLanguage);
  const [webFullscreen, setWebFullscreen] = useState(false);
  const [operationGuideOpen, setOperationGuideOpen] = useState(false);
  const [externalPlayerDialogOpen, setExternalPlayerDialogOpen] = useState(false);
  const [danmakuComments, setDanmakuComments] = useState<DanmakuComment[]>([]);
  const [danmakuSourceName, setDanmakuSourceName] = useState<string>();
  const [danmakuVideoSrc, setDanmakuVideoSrc] = useState<string>();
  const [selectedSubtitleId, setSelectedSubtitleId] = useState<number | null>(
    preferredSubtitle(subtitles, subtitlesEnabled, subtitleLanguage)?.id ?? null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const danmakuContainerRef = useRef<HTMLDivElement>(null);
  const danmakuRendererRef = useRef<Danmaku | undefined>(undefined);
  const videoRef = useRef<HTMLVideoElement>(null);
  const rendererRef = useRef<ASS | undefined>(undefined);
  const rendererSourceRef = useRef<string | undefined>(undefined);
  const rendererTrackIdRef = useRef<number | undefined>(undefined);
  const autoplayAttemptedRef = useRef(false);
  const subtitleSelectionTouchedRef = useRef(false);
  const subtitleDelayRef = useRef(0);
  subtitleDelayRef.current = subtitleDelay;
  const initialPositionSetRef = useRef(false);
  const completionReportedRef = useRef(false);
  const playbackProgressRef = useRef<{ progress: number; duration: number } | undefined>(undefined);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const selectedSubtitle = subtitles.find((track) => track.id === selectedSubtitleId);
  const toggleWebFullscreen = useCallback(() => {
    setWebFullscreen((value) => !value);
  }, []);
  const closeOperationGuide = useCallback(() => {
    setOperationGuideOpen(false);
  }, []);
  const closeExternalPlayerDialog = useCallback(() => {
    setExternalPlayerDialogOpen(false);
  }, []);
  const attemptPlay = useCallback(
    (video: HTMLVideoElement): void => {
      if (!autoplay || !video.paused || autoplayAttemptedRef.current) {
        return;
      }
      autoplayAttemptedRef.current = true;
      void video.play().catch(() => showToast(t('autoplayFailed'), 'error'));
    },
    [autoplay, showToast],
  );
  const loadDanmakuXml = useCallback(
    (source: string, sourceName: string): void => {
      const comments = parseDanmakuXml(source);
      setDanmakuComments(comments);
      setDanmakuSourceName(sourceName);
      setDanmakuVideoSrc(src);
      setDanmakuVisible(true);
      showToast(t('danmakuLoadedCount').replace('{count}', String(comments.length)), 'success');
    },
    [showToast, src],
  );
  useEffect(() => {
    autoplayAttemptedRef.current = false;
  }, [src]);

  useEffect(() => {
    if (videoRef.current) {
      attemptPlay(videoRef.current);
    }
  }, [attemptPlay, src]);

  useEffect(() => {
    document.body.style.overflow = webFullscreen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [webFullscreen]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = savedPlaybackRate;
    }
  }, [savedPlaybackRate, src]);

  useEffect(() => {
    initialPositionSetRef.current = false;
    completionReportedRef.current = false;
    playbackProgressRef.current = undefined;
    subtitleSelectionTouchedRef.current = false;
    setOperationGuideOpen(false);
    setDanmakuComments([]);
    setDanmakuSourceName(undefined);
    setDanmakuVideoSrc(undefined);
    setSelectedSubtitleId(preferredSubtitle(subtitles, subtitlesEnabled, subtitleLanguage)?.id ?? null);
  }, [src]);

  useEffect(() => {
    const reportFinalProgress = onProgressRef.current;
    return () => {
      const playbackProgress = playbackProgressRef.current;
      if (playbackProgress) {
        reportFinalProgress?.(playbackProgress.progress, playbackProgress.duration, true);
      }
    };
  }, [src]);

  const reportCompletion = useCallback(
    (video: HTMLVideoElement, ended = false): void => {
      if (completionReportedRef.current || !onCompleted) {
        return;
      }
      const completed =
        ended || (Number.isFinite(video.duration) && video.duration > 0 && video.currentTime / video.duration >= 0.9);
      if (!completed) {
        return;
      }
      completionReportedRef.current = true;
      onCompleted();
    },
    [onCompleted],
  );

  const reportProgress = useCallback((video: HTMLVideoElement, force = false): void => {
    if (
      !Number.isFinite(video.currentTime) ||
      video.currentTime < 0 ||
      !Number.isFinite(video.duration) ||
      video.duration <= 0
    ) {
      return;
    }
    const playbackProgress = { progress: video.currentTime, duration: video.duration };
    playbackProgressRef.current = playbackProgress;
    onProgressRef.current?.(playbackProgress.progress, playbackProgress.duration, force);
  }, []);

  const setInitialPosition = useCallback(
    (video: HTMLVideoElement): void => {
      if (initialPositionSetRef.current) {
        return;
      }
      if (initialTime && initialTime > 0 && Number.isFinite(video.duration)) {
        video.currentTime = Math.min(initialTime, Math.max(video.duration - 0.1, 0));
        initialPositionSetRef.current = true;
        return;
      }
      if (video.seekable.length > 0) {
        const start = video.seekable.start(0);
        if (Number.isFinite(start)) {
          video.currentTime = start;
          initialPositionSetRef.current = true;
        }
      }
    },
    [initialTime],
  );

  useEffect(() => {
    if (subtitles.length === 0) {
      setSelectedSubtitleId(null);
      return;
    }
    if (!subtitleSelectionTouchedRef.current) {
      setSelectedSubtitleId(preferredSubtitle(subtitles, subtitlesEnabled, subtitleLanguage)?.id ?? null);
      return;
    }
    if (selectedSubtitleId === null) {
      return;
    }
    if (!subtitles.some((track) => track.id === selectedSubtitleId && track.revision > 0)) {
      setSelectedSubtitleId(preferredSubtitle(subtitles, subtitlesEnabled, subtitleLanguage)?.id ?? null);
    }
  }, [selectedSubtitleId, subtitleLanguage, subtitles, subtitlesEnabled]);

  useEffect(() => {
    let disposed = false;
    const track = selectedSubtitle;
    const video = videoRef.current;
    const container = containerRef.current;
    if (!track || track.revision === 0 || !video || !container) {
      rendererRef.current?.destroy();
      rendererRef.current = undefined;
      rendererSourceRef.current = undefined;
      rendererTrackIdRef.current = undefined;
      return;
    }
    if (rendererSourceRef.current !== src || rendererTrackIdRef.current !== track.id) {
      rendererRef.current?.destroy();
      rendererRef.current = undefined;
    }
    const fetchTrack = async (): Promise<string | undefined> => {
      try {
        const response = await fetch(track.url, { cache: 'no-store' });
        if (!response.ok) {
          return undefined;
        }
        return response.text();
      } catch {
        return undefined;
      }
    };
    const replaceRenderer = (content: string): void => {
      const previous = rendererRef.current;
      rendererRef.current = undefined;
      previous?.destroy();
      const renderer = new ASS(content, video, { container });
      renderer.delay = subtitleDelayRef.current;
      rendererRef.current = renderer;
      rendererSourceRef.current = src;
      rendererTrackIdRef.current = track.id;
      if (!video.paused && !video.ended) {
        video.dispatchEvent(new Event('play'));
      }
    };
    const initialize = async (): Promise<void> => {
      if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
        await new Promise<void>((resolvePromise) => {
          video.addEventListener('loadedmetadata', () => resolvePromise(), { once: true });
        });
      }
      const content = await fetchTrack();
      if (disposed || !content) {
        return;
      }
      replaceRenderer(content);
    };
    void initialize();
    return () => {
      disposed = true;
    };
  }, [selectedSubtitle?.revision, selectedSubtitleId, src]);

  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.delay = subtitleDelay;
    }
  }, [subtitleDelay]);

  useEffect(() => {
    danmakuRendererRef.current?.destroy();
    danmakuRendererRef.current = undefined;
    const container = danmakuContainerRef.current;
    const video = videoRef.current;
    if (!container || !video || danmakuComments.length === 0 || danmakuVideoSrc !== src) {
      return;
    }
    const renderer = new Danmaku({
      container,
      media: video,
      engine: 'dom',
      speed: danmakuSpeed,
      comments: danmakuComments.map((comment) => ({
        mode: comment.mode,
        text: comment.text,
        time: comment.time,
        style: {
          color: comment.color,
          fontFamily: 'sans-serif',
          fontWeight: '600',
          lineHeight: '1.2',
        },
      })),
    });
    if (!danmakuVisible) {
      renderer.hide();
    }
    danmakuRendererRef.current = renderer;
    const resizeObserver = new ResizeObserver(() => renderer.resize());
    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
      if (danmakuRendererRef.current === renderer) {
        danmakuRendererRef.current = undefined;
      }
      renderer.destroy();
    };
  }, [danmakuComments, danmakuVideoSrc, src]);

  useEffect(() => {
    if (danmakuRendererRef.current) {
      danmakuRendererRef.current.speed = danmakuSpeed;
    }
  }, [danmakuSpeed]);

  useEffect(() => {
    if (danmakuVisible) {
      danmakuRendererRef.current?.show();
    } else {
      danmakuRendererRef.current?.hide();
    }
  }, [danmakuVisible]);

  useEffect(
    () => () => {
      rendererRef.current?.destroy();
      rendererRef.current = undefined;
    },
    [],
  );

  return (
    <PlayerProvider>
      <Container
        ref={containerRef}
        className={`video-player ${webFullscreen ? 'web-fullscreen' : ''}`.trim()}
        tabIndex={0}
      >
        <Video
          ref={videoRef}
          src={src}
          preload="auto"
          playsInline
          onLoadedMetadata={(event) => {
            setInitialPosition(event.currentTarget);
            attemptPlay(event.currentTarget);
          }}
          onCanPlay={(event) => {
            setInitialPosition(event.currentTarget);
            attemptPlay(event.currentTarget);
          }}
          onTimeUpdate={(event) => {
            reportProgress(event.currentTarget);
            reportCompletion(event.currentTarget);
          }}
          onPause={(event) => reportProgress(event.currentTarget, true)}
          onEnded={(event) => {
            reportProgress(event.currentTarget, true);
            reportCompletion(event.currentTarget, true);
            onEnded?.();
          }}
          onError={onError}
        />
        <div
          ref={danmakuContainerRef}
          className="video-danmaku-layer"
          style={
            {
              '--danmaku-filter': danmakuShadowEnabled ? DANMAKU_DROP_SHADOW : 'none',
              '--danmaku-font-size': `${danmakuFontSize}px`,
              '--danmaku-text-shadow': danmakuOutlineEnabled ? DANMAKU_OUTLINE_TEXT_SHADOW : 'none',
            } as CSSProperties
          }
          aria-hidden="true"
        />
        <PlayerInteractions containerRef={containerRef} disabled={operationGuideOpen || externalPlayerDialogOpen} />
        <PlayerControls
          containerRef={containerRef}
          danmaku={{
            commentCount: danmakuComments.length,
            fontSize: danmakuFontSize,
            outlineEnabled: danmakuOutlineEnabled,
            shadowEnabled: danmakuShadowEnabled,
            sourceName: danmakuSourceName,
            speed: danmakuSpeed,
            visible: danmakuVisible,
            onFontSizeChange: setDanmakuFontSize,
            onLoadXml: loadDanmakuXml,
            onOutlineChange: setDanmakuOutlineEnabled,
            onShadowChange: setDanmakuShadowEnabled,
            onSpeedChange: setDanmakuSpeed,
            onVisibleChange: setDanmakuVisible,
          }}
          expectedDuration={duration}
          webFullscreen={webFullscreen}
          onToggleWebFullscreen={toggleWebFullscreen}
          subtitles={subtitles}
          subtitlesComplete={subtitlesComplete}
          selectedSubtitleId={selectedSubtitleId}
          subtitleDelay={subtitleDelay}
          onCopyVideoLink={onCopyVideoLink}
          onDownloadVideoFile={onDownloadVideoFile}
          onShowExternalPlayer={() => setExternalPlayerDialogOpen(true)}
          onShowOperationGuide={() => setOperationGuideOpen(true)}
          onSubtitleDelayChange={setSubtitleDelay}
          onSelectSubtitle={(id) => {
            subtitleSelectionTouchedRef.current = true;
            setSelectedSubtitleId(id);
          }}
        />
        <PlayerOperationGuide open={operationGuideOpen} onClose={closeOperationGuide} />
        <ExternalPlayerDialog
          src={src}
          open={externalPlayerDialogOpen}
          onClose={closeExternalPlayerDialog}
          onCopyVideoLink={onCopyVideoLink}
          onDownloadM3uPlaylist={onDownloadM3uPlaylist}
        />
      </Container>
    </PlayerProvider>
  );
};
